import React, { useMemo } from 'react';
import classNames from 'classnames';
import { TooltipPosition } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipPositioning';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { MinusCircleIcon } from '@heroicons/react/20/solid';
import { Pill } from 'apps/sigma2-ui/src/components/Pill';
import { Over40IconSvg } from 'apps/sigma2-ui/src/components/svg/Over40Icon';
import { Under40IconSvg } from 'apps/sigma2-ui/src/components/svg/Under40Icon';
import { PillBorder } from 'apps/sigma2-ui/src/components/PillBorder';
import { LeftRightPager } from 'apps/sigma2-ui/src/components/LeftRightPager';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { CopilotLineGraphTooltipButton } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { IconAddCircleOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconAddCircleOutline';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';

interface CopilotLineGraphTooltipViewProps {
  onClose: () => void;
  subject: SlatePoolSubject;
  tooltipPosition: TooltipPosition;
  onAddToSlate: () => void;
  onRemoveFromSlate: () => void;
  onGoLeft: () => void;
  onGoRight: () => void;
  isLeftDisabled: boolean;
  isRightDisabled: boolean;
}

export const CopilotLineGraphTooltipView = (
  props: CopilotLineGraphTooltipViewProps,
) => {
  const language = useLanguage();

  const { getLevelByColumnValue } = useDimensionConfig();

  const subject = props.subject;

  const positioningClassName = useMemo(() => {
    switch (props.tooltipPosition) {
      case 'bottom_left':
        return 'bottom-0 left-0';
      case 'bottom_middle':
        return 'bottom-0 left-1/2 transform -translate-x-1/2';
      case 'bottom_right':
        return 'bottom-0 right-0';
      case 'middle_left':
        return 'top-1/2 left-0 transform -translate-y-1/2';
      case 'middle_middle':
        return 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'middle_right':
        return 'top-1/2 right-0 transform -translate-y-1/2';
      case 'top_left':
        return 'top-0 left-0';
      case 'top_middle':
        return 'top-0 left-1/2 transform -translate-x-1/2';
      case 'top_right':
        return 'top-0 right-0';
    }
  }, [props.tooltipPosition]);

  return (
    <>
      <div
        className={classNames(
          'z-tooltip absolute w-[256px] bg-blue-100 bg-opacity-90 rounded-lg shadow-12px mt-6',
          positioningClassName,
        )}
      >
        <div>
          <div className="flex p-3 justify-between items-start">
            <div className="w-full">
              <div className="flex justify-between">
                <Heading
                  level={4}
                  textColor="text-black"
                  className="capitalize"
                >
                  {subject.jobTitle}
                </Heading>
                <button
                  data-qa="closeButton"
                  onClick={props.onClose}
                  className="w-4 h-4 bg-white rounded-full flex flex-row justify-center items-center"
                >
                  <IconCloseSvg svg={{ className: 'text-gray-500 w-2 h-2' }} />
                </button>
              </div>
              <Paragraph textColor="text-gray-600">
                {language.hiringCopilotTooltipID({ id: subject.id })}
              </Paragraph>
              <div className="flex items-end pt-2 gap-1">
                <Paragraph textColor="text-black">
                  {language.hiringCopilotTooltipReadiness()}
                </Paragraph>
                <Paragraph
                  textColor={
                    subject.readinessRating >= 80
                      ? 'text-green-600'
                      : 'text-black'
                  }
                >
                  <b>{subject.readinessRating}%</b>
                </Paragraph>
              </div>
              <CopilotLineGraphTooltipButton
                variant={
                  props.subject.isInSlate
                    ? LegacyButtonVariant.TERTIARY_DARK
                    : LegacyButtonVariant.PRIMARY_DARK
                }
                onClick={
                  props.subject.isInSlate
                    ? props.onRemoveFromSlate
                    : props.onAddToSlate
                }
                icon={
                  props.subject.isInSlate ? (
                    <MinusCircleIcon className="w-4 h-4 text-white" />
                  ) : (
                    <IconAddCircleOutlineSvg svg={{ className: 'h-4 w-4' }} />
                  )
                }
                text={
                  props.subject.isInSlate
                    ? language.hiringCopilotRemoveFromSlate()
                    : language.hiringCopilotAddToSlate()
                }
              />
            </div>
          </div>
        </div>
        <div className="flex bg-highlight rounded-b-lg h-[3rem] justify-center items-center">
          <LeftRightPager
            isPreviousDisabled={props.isLeftDisabled}
            onPrevious={props.onGoLeft}
            isNextDisabled={props.isRightDisabled}
            onNext={props.onGoRight}
          />
        </div>
      </div>
    </>
  );
};
